import { Controller } from "@hotwired/stimulus"
import { Chart, BarController, BarElement, LineController, LineElement, PointElement, LinearScale, TimeScale, Legend, Tooltip } from "chart.js"
import "chartjs-adapter-luxon"

Chart.register(BarController, BarElement, LineController, LineElement, PointElement, LinearScale, TimeScale, Legend, Tooltip)

// Live campaign chart: polls the metrics JSON endpoint and redraws one panel — sent per bucket as
// bars on the count axis (left) with the click-through, bot and error rates as lines on a percent
// axis; the click and bot samples are one per recipient, so the rates are distinct clickers over
// sends like the page's tiles, not hits.
// axis (right). Two units, two axes, on purpose: every line is a percentage of sends, running
// from the campaign's start, so lines compare with lines and bars with bars. Points sit on a
// complete grid (idle buckets are zeros); the legend names the bucket, which grows with the span.
// Times are in the `zone` value (Eastern, like every time the portal prints), not the browser's zone.
// Colors come from the CSS tokens in application.tailwind.css and are re-read when the theme toggles.
// Fixed axis widths: an axis sized to its own tick labels would move the plot's edges from poll to poll.
const Y_AXIS_WIDTH = 48
const BAR_ALPHA = "66"   // the bars sit behind the lines

export default class extends Controller {
  static targets = ["chart", "span", "status"]
  // live=false (finished campaigns): load once, no polling.
  static values = { url: String, interval: { type: Number, default: 5000 }, live: { type: Boolean, default: true },
                    zone: { type: String, default: "America/New_York" } }

  // [key, source, name, token]: source "series" = counts per bucket (bars), "rates" = percent of sends (lines)
  static SERIES = [
    ["sent",  "series", "Sent",       "--series-sent"],
    ["ctr",   "rates",  "CTR",        "--series-click"],
    ["bot",   "rates",  "Bot rate",   "--series-bot"],
    ["error", "rates",  "Error rate", "--series-error"],
  ]

  connect() {
    if (!this.hasChartTarget) return   // metrics unavailable: the view renders a notice instead
    this.chart = this.build(this.chartTarget)
    this.onTheme = () => this.recolor()
    window.addEventListener("theme:change", this.onTheme)
    this.load()
    if (this.liveValue) this.timer = setInterval(() => this.load(), this.intervalValue)
  }

  disconnect() {
    clearInterval(this.timer)
    this.abort?.abort()
    window.removeEventListener("theme:change", this.onTheme)
    this.chart?.destroy()
    this.chart = null
  }

  change() { this.load() }

  build(canvas) {
    const t = this.tokens()
    const percent = v => `${v}%`
    return new Chart(canvas, {
      type: "bar",
      data: { datasets: this.constructor.SERIES.map(([key, source, name, token]) => this.dataset(key, source, name, token, t)) },
      options: {
        animation: false, responsive: true, maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        scales: {
          // Luxon formats and aligns the ticks in `zone`; 24-hour labels like the `eastern` helper's.
          x: { type: "time", adapters: { date: { zone: this.zoneValue } }, offset: true,
               time: { tooltipFormat: "MMM d HH:mm", displayFormats: { minute: "HH:mm", hour: "HH:mm", day: "MMM d" } },
               grid: { color: t.grid, drawTicks: false }, border: { display: false },
               ticks: { color: t.tick, font: { size: 11 }, maxRotation: 0, autoSkipPadding: 24 } },
          y: { beginAtZero: true, afterFit: axis => { axis.width = Y_AXIS_WIDTH },
               ticks: { precision: 0, color: t.tick, font: { size: 11 }, maxTicksLimit: 4 },
               grid: { color: t.grid, drawTicks: false }, border: { display: false } },
          pct: { position: "right", beginAtZero: true, afterFit: axis => { axis.width = Y_AXIS_WIDTH },
                 ticks: { color: t.tick, font: { size: 11 }, maxTicksLimit: 4, callback: percent },
                 grid: { drawOnChartArea: false, drawTicks: false }, border: { display: false } },
        },
        plugins: {
          legend: { position: "top", align: "end", labels: { color: t.tick, boxWidth: 10, boxHeight: 10, usePointStyle: true, pointStyle: "rectRounded", font: { size: 11 } } },
          tooltip: { backgroundColor: t.panel, titleColor: t.ink, bodyColor: t.ink, borderColor: t.line, borderWidth: 1, padding: 10, cornerRadius: 6, displayColors: true, boxPadding: 4,
                     callbacks: { label: c => `${c.dataset.label}: ${c.dataset.yAxisID === "pct" ? percent(c.parsed.y) : c.parsed.y}` } },
        }
      }
    })
  }

  dataset(key, source, name, token, t) {
    const base = { key, source, name, label: name, token, data: [] }
    if (source === "series") {
      return { ...base, type: "bar", yAxisID: "y", order: 1, backgroundColor: t.get(token) + BAR_ALPHA,
               borderWidth: 0, borderRadius: 2, borderSkipped: "bottom", barThickness: "flex", barPercentage: 0.8, categoryPercentage: 1 }
    }
    // Monotone interpolation: a Bézier tension overshoots, dipping below zero beside a spike.
    return { ...base, type: "line", yAxisID: "pct", order: 2, borderColor: t.get(token), backgroundColor: t.get(token),
             borderWidth: 2, pointRadius: 0, pointHitRadius: 12, cubicInterpolationMode: "monotone" }
  }

  recolor() {
    const t = this.tokens()
    const c = this.chart
    c.data.datasets.forEach(ds => {
      if (ds.type === "bar") ds.backgroundColor = t.get(ds.token) + BAR_ALPHA
      else { ds.borderColor = t.get(ds.token); ds.backgroundColor = t.get(ds.token) }
    })
    c.options.scales.x.grid.color = t.grid; c.options.scales.y.grid.color = t.grid
    for (const axis of ["x", "y", "pct"]) c.options.scales[axis].ticks.color = t.tick
    c.options.plugins.legend.labels.color = t.tick
    Object.assign(c.options.plugins.tooltip, { backgroundColor: t.panel, titleColor: t.ink, bodyColor: t.ink, borderColor: t.line })
    c.update()
  }

  tokens() {
    const style = getComputedStyle(document.documentElement)
    const read = name => style.getPropertyValue(name).trim()
    return { get: read, grid: read("--chart-grid"), tick: read("--chart-tick"), panel: read("--panel"), ink: read("--ink"), line: read("--line") }
  }

  // One request in flight at a time: a new load (span change, next tick) aborts the previous
  // one, so a slow 7d response can never overwrite a later 1h selection or touch a destroyed chart.
  async load() {
    if (!this.chart) return
    const span = this.hasSpanTarget ? this.spanTarget.value : "1h"
    this.abort?.abort()
    const abort = (this.abort = new AbortController())
    try {
      const res = await fetch(`${this.urlValue}?span=${span}`, { headers: { Accept: "application/json" }, signal: abort.signal })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      if (abort.signal.aborted || !this.chart) return
      // Counts are per bucket, which grows with the span: the legend names it so the count axis
      // means the same thing across spans. The rates run from the campaign's start.
      const per = this.perBucket(data.bucket_seconds)
      this.chart.data.datasets.forEach(ds => {
        ds.label = ds.source === "rates" ? `${ds.name} to date` : `${ds.name} / ${per}`
        ds.data = (data[ds.source]?.[ds.key] || []).map(([t, v]) => ({ x: t, y: v }))
      })
      this.chart.options.scales.x.min = data.from
      this.chart.options.scales.x.max = data.to
      this.chart.update()
      // A finished campaign loads once: "updated" would suggest the run is still changing.
      if (this.hasStatusTarget) this.statusTarget.textContent = this.liveValue ? `updated ${new Date().toLocaleTimeString()}` : ""
    } catch (e) {
      if (e.name === "AbortError") return
      if (this.hasStatusTarget) this.statusTarget.textContent = `metrics unavailable (${e.message})`
    }
  }

  // "min", "5 min", "hour": the unit behind every value at this span.
  perBucket(seconds) {
    if (seconds % 3600 === 0) return seconds === 3600 ? "hour" : `${seconds / 3600} h`
    return seconds === 60 ? "min" : `${seconds / 60} min`
  }
}
